<script lang="ts" setup>
import type { DropdownItem, PopperOptions } from "#ui/types";
import { twMerge, twJoin } from "tailwind-merge";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useUI } from "#ui/composables/useUI";
import { toRef } from "vue";
import appConfig from "#build/app.config";
import { dropdown } from "#ui/ui.config";
import { mergeConfig } from "#ui/utils";

const config = mergeConfig(
    appConfig.ui.strategy,
    appConfig.ui.dropdown,
    dropdown,
);

const open = defineModel<boolean>("open");

const props = withDefaults(
    defineProps<{
        items?: DropdownItem[][];
        mode?: "click" | "hover";
        disabled?: boolean;
        popper?: PopperOptions;
        openDelay?: number;
        closeDelay?: number;
        class?: any;
        ui?: any;
    }>(),
    {
        items: () => [],
        mode: "click",
        open: void 0,
        disabled: false,
        popper: () => ({}),
        class: () => "",
        ui: () => ({}),
    },
);

const { ui, attrs } = useUI(
    "dropdown",
    toRef(props, "ui"),
    config,
    toRef(props, "class"),
);

const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
};
</script>

<template>
    <UDropdown
        :items="props.items"
        :mode="props.mode"
        v-model:open="open"
        :disabled="props.disabled"
        :popper="props.popper"
        :openDelay="props.openDelay"
        :closeDelay="props.closeDelay"
        :class="props.class"
        :ui="props.ui"
        @click="onClick"
    >
        <template v-for="(_, slotName) in $slots" v-slot:[slotName]="slotProps">
            <template v-if="slotName !== 'item'">
                <slot
                    :name="slotName"
                    v-bind="slotProps ?? {}"
                    :open-dropdown="() => (open = true)"
                    :close-dropdown="() => (open = false)"
                    :toggle="() => (open = !open)"
                />
            </template>
        </template>
        <template #item="{ item }">
            <slot name="item" v-bind="{ item }">
                <FontAwesomeIcon
                    v-if="item.icon"
                    :icon="item.icon"
                    :class="
                        twMerge(
                            twJoin(
                                ui.item.icon.base,
                                item.active
                                    ? ui.item.icon.active
                                    : ui.item.icon.inactive,
                            ),
                            item.iconClass,
                        )
                    "
                />
                <UAvatar
                    v-else-if="item.avatar"
                    v-bind="{
                        size: ui.item.avatar.size,
                        ...item.avatar,
                    }"
                    :class="ui.item.avatar.base"
                />

                <span :class="twMerge(ui.item.label, item.labelClass)">{{
                    item.label
                }}</span>

                <span v-if="item.shortcuts?.length" :class="ui.item.shortcuts">
                    <UKbd v-for="shortcut of item.shortcuts" :key="shortcut">{{
                        shortcut
                    }}</UKbd>
                </span>
            </slot>
        </template>
    </UDropdown>
</template>
